// Facture d'une société ou d'une assurance (05/10/2026) : rubriques des prestations, identité de chaque patient,
// devise d'impression. Fonctions pures, partagées par le détail à l'écran et l'impression.
import { NumberToLetter } from 'convertir-nombre-lettre'

// Rubriques dans l'ordre de la facture : la consultation d'abord.
export const RUBRIQUES = [
    { cle: 'consultation', libelle: 'Consultation', types: ['bon_consultation', 'consultation'] },
    { cle: 'analyse', libelle: 'Analyses', types: ['analyse'] },
    { cle: 'bilan', libelle: 'Bilans de santé', types: ['bilan_sante'] },
    { cle: 'examen', libelle: 'Examens', types: ['examen', 'imagerie'] },
    { cle: 'acte', libelle: 'Actes', types: ['acte', 'chirurgie'] },
    { cle: 'soin', libelle: 'Soins', types: ['soin', 'soins'] },
    { cle: 'hospitalisation', libelle: 'Hospitalisation', types: ['chambre', 'hospitalisation', 'sejour'] },
    { cle: 'medicament', libelle: 'Médicaments', types: ['medicament'] },
    { cle: 'consommable', libelle: 'Consommables', types: ['consommable'] },
]
const AUTRES = { cle: 'autre', libelle: 'Autres prestations' }

export function rubriqueDe(data_type) {
    const type = String(data_type || '').toLowerCase()
    return RUBRIQUES.find(rubrique => rubrique.types.indexOf(type) >= 0) || AUTRES
}

/** Lignes d'un patient regroupées par rubrique, dans l'ordre de la facture ; chaque groupe porte son total. */
export function grouperParRubrique(lignes) {
    const groupes = {}
    ;(lignes || []).forEach(ligne => {
        const rubrique = rubriqueDe(ligne.data_type)
        if (!groupes[rubrique.cle]) groupes[rubrique.cle] = { cle: rubrique.cle, libelle: rubrique.libelle, lignes: [], total: 0 }
        groupes[rubrique.cle].lignes.push(ligne)
        groupes[rubrique.cle].total += Number(ligne.part_assureur) || 0
    })
    return RUBRIQUES.concat([AUTRES]).filter(rubrique => groupes[rubrique.cle]).map(rubrique => groupes[rubrique.cle])
}

/** Numéros de bon non vides d'un patient, dans l'ordre, sans doublon. */
export function numerosBon(lignes) {
    const numeros = []
    ;(lignes || []).forEach(ligne => {
        const numero = String(ligne.num_bon_societe == null ? '' : ligne.num_bon_societe).trim()
        if (numero !== '' && numeros.indexOf(numero) === -1) numeros.push(numero)
    })
    return numeros.join(', ')
}

/**
 * Identité imprimée après le nom : matricule (s'il existe), Agent ou lien avec le travailleur, n° de bon, âge / sexe.
 * Rend une liste de mentions ; une information absente n'apparaît pas.
 */
export function mentionsIdentite(lignes) {
    const ligne = (lignes || []).find(l => l.patient_lien_libelle || l.patient_matricule || l.patient_age || l.patient_sexe) || {}
    const mentions = []
    if (ligne.patient_matricule) mentions.push('Matricule ' + ligne.patient_matricule)
    if (ligne.patient_lien_libelle) mentions.push(ligne.patient_lien_libelle)
    const bons = numerosBon(lignes)
    if (bons) mentions.push('N° bon : ' + bons)
    const age_sexe = [ligne.patient_age, ligne.patient_sexe].filter(Boolean).join(' / ')
    if (age_sexe) mentions.push(age_sexe)
    return mentions
}

// ---------------------------------------------------------------- Devise d'impression

/** Modes proposés : principale seule, secondaire seule, et chacune avec sa conversion dans l'autre. */
export function modesDevise(currency_info) {
    const principale = (currency_info && currency_info.principal_currency) || ''
    const secondaire = (currency_info && currency_info.secondary_currency) || ''
    const modes = [{ cle: 'principale', libelle: principale + ' seul' }]
    if (secondaire && Number(currency_info.rate) > 0) {
        modes.push({ cle: 'secondaire', libelle: secondaire + ' seul' })
        modes.push({ cle: 'principale_conversion', libelle: principale + ' avec conversion en ' + secondaire })
        modes.push({ cle: 'secondaire_conversion', libelle: secondaire + ' avec conversion en ' + principale })
    }
    return modes
}

function decimales(devise) {
    return ['CDF', 'XOF', 'XAF', 'F.CFA', 'FCFA', 'GNF', 'RWF', 'BIF'].indexOf(String(devise || '').toUpperCase()) >= 0 ? 0 : 2
}

function arrondir(valeur, chiffres) {
    const facteur = Math.pow(10, chiffres)
    return Math.round((Number(valeur) || 0) * facteur) / facteur
}

/**
 * Un montant enregistré en devise principale, exprimé selon le mode choisi.
 * taux = valeur d'une unité de devise secondaire en devise principale (1 USD = 2 300 CDF) : principale = secondaire × taux.
 * Rend { valeur, devise, conversion: { valeur, devise } | null }.
 */
export function montantSelonMode(montant_principal, mode, currency_info) {
    const principale = currency_info.principal_currency
    const secondaire = currency_info.secondary_currency
    const taux = Number(currency_info.rate) || 0
    const en_principale = { valeur: arrondir(montant_principal, decimales(principale)), devise: principale }
    if (!secondaire || taux <= 0 || mode === 'principale' || !mode) return Object.assign({}, en_principale, { conversion: null })
    const en_secondaire = { valeur: arrondir((Number(montant_principal) || 0) / taux, decimales(secondaire)), devise: secondaire }
    if (mode === 'secondaire') return Object.assign({}, en_secondaire, { conversion: null })
    if (mode === 'principale_conversion') return Object.assign({}, en_principale, { conversion: en_secondaire })
    return Object.assign({}, en_secondaire, { conversion: en_principale })
}

/** « 395 350 CDF », « 171,89 USD » : séparateurs français, décimales fixes selon la devise. */
export function formaterMontant(montant) {
    const chiffres = decimales(montant.devise)
    const texte = (Number(montant.valeur) || 0).toLocaleString('fr-FR', { minimumFractionDigits: chiffres, maximumFractionDigits: chiffres })
    // Espaces insécables du séparateur de milliers (U+202F, U+00A0) remplacées par des espaces simples.
    return texte.replace(new RegExp('[\\u202f\\u00a0]', 'g'), ' ') + ' ' + montant.devise
}

const NOMS_DEVISE = { CDF: ['franc congolais', 'francs congolais'], USD: ['dollar américain', 'dollars américains'],
    EUR: ['euro', 'euros'], XOF: ['franc CFA', 'francs CFA'], XAF: ['franc CFA', 'francs CFA'], 'F.CFA': ['franc CFA', 'francs CFA'] }

/** « Trois cent quatre-vingt-quinze mille trois cent cinquante francs congolais » ; centimes en toutes lettres. */
export function montantEnLettres(montant) {
    const devise = String(montant.devise || '').toUpperCase()
    const valeur = Math.abs(Number(montant.valeur) || 0)
    const entier = Math.floor(valeur)
    const centimes = Math.round((valeur - entier) * 100)
    const noms = NOMS_DEVISE[devise] || [montant.devise, montant.devise]
    const lettres = entier === 0 ? 'zéro' : NumberToLetter(entier)
    let texte = lettres + ' ' + (entier > 1 ? noms[1] : noms[0])
    if (centimes > 0) texte += ' et ' + NumberToLetter(centimes) + ' cent' + (centimes > 1 ? 's' : '')
    return texte.charAt(0).toUpperCase() + texte.slice(1)
}

/** « 1 USD = 2 300 CDF » quand la facture fait intervenir la devise secondaire. */
export function mentionTaux(mode, currency_info) {
    if (!mode || mode === 'principale' || !(Number(currency_info.rate) > 0)) return ''
    return '1 ' + currency_info.secondary_currency + ' = ' + formaterMontant({ valeur: Number(currency_info.rate), devise: currency_info.principal_currency })
}
